/**
 * Admin Service
 * Handles admin-related API calls including usage reset
 */

import { API_CONFIG } from '../config/constants';

const API_URL = API_CONFIG.BASE_URL;

interface ResetUsageResponse {
  success: boolean;
  message?: string;
  error?: string;
  detail?: string;
  user?: {
    id: number;
    email: string;
    monthly_simulation_count: number;
    monthly_group_chats_used: number;
    monthly_simulation_chats_used: number;
    simulation_reset_date: string;
  };
}

interface CheckAuthorizationResponse {
  authorized: boolean;
}

interface SwitchToFreeResponse {
  success: boolean;
  message?: string;
  error?: string;
  detail?: string;
  user?: {
    id: number;
    email: string;
    subscription_plan: string;
    subscription_status: string;
  };
}

/**
 * Check if the current user is authorized to reset their usage
 */
export const checkResetAuthorization = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/admin/check-reset-authorization`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to check reset authorization');
      return false;
    }

    const data: CheckAuthorizationResponse = await response.json();
    return data.authorized;
  } catch (error) {
    console.error('Error checking reset authorization:', error);
    return false;
  }
};

/**
 * Reset the current user's usage
 */
export const resetMyUsage = async (): Promise<ResetUsageResponse> => {
  try {
    const response = await fetch(`${API_URL}/admin/reset-my-usage`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data: ResetUsageResponse = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.detail || 'Failed to reset usage',
      };
    }

    return data;
  } catch (error) {
    console.error('Error resetting usage:', error);
    return {
      success: false,
      error: 'An error occurred while resetting usage',
    };
  }
};

/**
 * Switch the current user to free tier
 */
export const switchToFreeTier = async (): Promise<SwitchToFreeResponse> => {
  try {
    const response = await fetch(`${API_URL}/admin/switch-to-free-tier`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data: SwitchToFreeResponse = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.detail || 'Failed to switch to free tier',
      };
    }

    return data;
  } catch (error) {
    console.error('Error switching to free tier:', error);
    return {
      success: false,
      error: 'An error occurred while switching to free tier',
    };
  }
};

